import { Injectable, OnDestroy } from '@angular/core';
import { NgRedux } from '@angular-redux/store';
import { Subscription } from 'rxjs';
import { distinctUntilChanged, take } from 'rxjs/operators';

import { IAppState } from '@core/redux/stores';
import { ApiRoutes } from '@shared/models/configs/api.endpoints';
import { ModalService } from '@core/services/modal.service';
import { ProxyService } from '@core/services/proxy.service';

const TNC_MODAL_AFTER_SIGNIN_MS = 700;
const TNC_RECHECK_INTERVAL_MS = 20 * 60 * 1000; // in memory cache of TNC version is valid for 20 minutes;
const TNC_PENDING_RETRY_MS = 2 * 1000; // If last attempt was not successful, or customer tries to bypass this

interface AuthTncSnapshot {
  isLoggedIn: boolean;
  userId: string | null;
}

@Injectable({
  providedIn: 'root',
})
export class TncVersionCheckCoordinatorService implements OnDestroy {
  private readonly authSubscription = new Subscription();
  private activeUserId: string | null = null;
  private recheckTimeoutId: any = null;

  constructor(
    private ngRedux: NgRedux<IAppState>,
    private proxy: ProxyService,
    private modalService: ModalService,
  ) {
    this.authSubscription.add(
      this.ngRedux
        .select((state: IAppState): AuthTncSnapshot => ({
          isLoggedIn: !!state?.auth?.isLoggedIn,
          userId: state?.user?.id != null && state.user.id !== ''
            ? String(state.user.id)
            : null,
        }))
        .pipe(
          distinctUntilChanged(
            (a, b) => a.isLoggedIn === b.isLoggedIn && a.userId === b.userId,
          ),
        )
        .subscribe((snapshot) => this.onAuthSnapshot(snapshot)),
    );
  }

  ngOnDestroy(): void {
    this.authSubscription.unsubscribe();
    clearTimeout(this.recheckTimeoutId);
  }

  notifyTermsAccepted(): void {
    if (this.activeUserId) {
      this.scheduleRecheck(this.activeUserId);
    }
  }

  private onAuthSnapshot(snapshot: AuthTncSnapshot): void {
    const { isLoggedIn, userId } = snapshot;
    if (!isLoggedIn) {
      this.activeUserId = null;
      clearTimeout(this.recheckTimeoutId);
      return;
    }
    if (!userId || this.activeUserId === userId) {
      return;
    }
    this.activeUserId = userId;
    this.fetchAndEvaluate(userId);
  }

  private fetchAndEvaluate(userId: string): void {
    clearTimeout(this.recheckTimeoutId);
    this.proxy
      .httpGet(ApiRoutes.TNC_VERSION_LATEST)
      .pipe(take(1))
      .subscribe({
        next: (response) => {
          const isLatest = response.body.valueOf() as boolean;
          this.scheduleRecheck(userId, isLatest ? TNC_RECHECK_INTERVAL_MS : TNC_PENDING_RETRY_MS);
          if (isLatest === false && !this.modalService.isModalOpen()) {
            this.showTncModal();
          }
        },
        error: () => {},
      });
  }

  private scheduleRecheck(userId: string, intervalMs = TNC_RECHECK_INTERVAL_MS): void {
    clearTimeout(this.recheckTimeoutId);
    this.recheckTimeoutId = setTimeout(() => {
      if (this.activeUserId === userId) {
        this.fetchAndEvaluate(userId);
      }
    }, intervalMs);
  }

  private showTncModal(): void {
    setTimeout(() => {
      try {
        this.modalService.setModalSelector('app-tnc-update');
        this.modalService.showModal(false);
      } catch {
        //standardModal not yet ready; pending retry will handle it
      }
    }, TNC_MODAL_AFTER_SIGNIN_MS);
  }
}
